import LoadingBox from 'components/Common/Loading/LoadingBox'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import Divider from 'LuxKit/Divider'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import Group from 'components/utils/Group'
import config from 'constants/config'
import { useAppSelector } from 'hooks/reduxHooks'
import useLuxPlusPreferredPaymentCard from 'luxPlus/hooks/useLuxPlusPreferredPaymentMethod'
import { getIsPendingRenewalLuxPlusMember } from 'luxPlus/selectors/featureToggle'
import { FormattedMessage, useIntl } from 'react-intl'

interface Props {
  onUpdatePaymentMethod?: () => void
  item: App.MemberSubscriptionItem
}

function LuxPlusPreferredPaymentMethod({ onUpdatePaymentMethod, item }: Props) {
  const intl = useIntl()
  const isPendingRenewalLuxPlusMember = useAppSelector(getIsPendingRenewalLuxPlusMember)
  const nextChargeAttemptDate = item.nextChargeAttemptDate
  const finalChargeAttemptDate = item.finalChargeAttemptDate
  const hasAutoRenewal = item.renewalStatus === 'ALLOW_RENEWAL'

  const preferredPaymentCard = useLuxPlusPreferredPaymentCard()

  const card = preferredPaymentCard?.data
  const error = preferredPaymentCard?.error
  const isLoading = preferredPaymentCard?.fetching ?? false
  const hasNoCard = !card && error

  return <>
    <VerticalSpacer gap={12}>
      <BodyText variant="medium" weight="bold">{intl.formatMessage({
        id: 'xiFr9Pr-SwrMzZAEXHYR',
        defaultMessage: 'Preferred payment method',
        description: 'Section heading for preferred payment method in LuxPlus+ account management',
      })}
      </BodyText>
      <Group direction="horizontal" horizontalAlign="space-between" gap={16}>
        <Group direction="horizontal" gap={8} verticalAlign="center">
          {card && <>
            <img
              alt={intl.formatMessage(
                {
                  id: '-AGhDg8G8LqN0VN4U8Oq',
                  defaultMessage: '{cardBrand} card ending with {lastFourDigits}',
                  description: 'Alt text for the payment card logo image in LuxPlus+ preferred payment method',
                },
                { cardBrand: card.brand, lastFourDigits: card.last4 },
              )}
              height="24px"
              src={card.logo}
            />
            <BodyText variant="medium">{card.maskedPan}</BodyText>
          </>}
          {isLoading && <>
            <LoadingBox width={40} height={24} />
            <div><TextLoadingBox width="4ch" /> <TextLoadingBox width="4ch" /> <TextLoadingBox width="4ch" /> <TextLoadingBox width="4ch" /></div>
          </>}
          {hasNoCard && <BodyText variant="medium">{intl.formatMessage({
            id: '6tZJE1CGqt1ygDy64DIE',
            defaultMessage: 'No card provided',
            description: 'Fallback text when no payment card is on file for LuxPlus+ membership',
          })}
          </BodyText>}
        </Group>
        {config.LUXURY_PLUS_UPDATE_PAYMENT_METHOD_ENABLED && <TextLink size="medium" onClick={onUpdatePaymentMethod}>{intl.formatMessage({
          id: 'GuU_YHtWQULB9OKHMgUW',
          defaultMessage: 'Update',
          description: 'Link text to update preferred payment method for LuxPlus+ membership',
        })}
        </TextLink>}
      </Group>
    </VerticalSpacer>
    {(isPendingRenewalLuxPlusMember && hasAutoRenewal && nextChargeAttemptDate !== undefined) && <MessageBanner
      kind="warning"
      density="compact"
      heading={intl.formatMessage({
        id: 'JPhdUWWm9dJCVRHTz3QN',
        defaultMessage: 'Renewal attempt failed',
        description: 'Warning banner heading when LuxPlus+ membership renewal payment attempt fails',
      })}
    >
      <FormattedMessage
        id="WybwZUjRTQ1n2eZchTUr"
        defaultMessage="Initial payment failed. You will be charged on {chargeDate} on your preferred payment method above."
        description="Warning message when LuxPlus+ membership renewal payment fails, showing next charge attempt date"
        values={{
          chargeDate: <FormatDateTime value={nextChargeAttemptDate} dateStyle="medium" />,
        }}
      />
    </MessageBanner>}
    <Divider kind="primary" />
    {(isPendingRenewalLuxPlusMember && hasAutoRenewal && finalChargeAttemptDate !== undefined) &&
      <Group direction="horizontal" horizontalAlign="space-between" gap={16}>
        <BodyText variant="medium" weight="bold" colour="urgency">{intl.formatMessage({
          id: 'Zl0I4OHwMbuCp9_aUvIr',
          defaultMessage: 'Payment due',
          description: 'Label for payment due date when LuxPlus+ membership renewal is pending',
        })}
        </BodyText>
        <BodyText variant="medium" colour="urgency"><FormatDateTime value={finalChargeAttemptDate} dateStyle="medium" /></BodyText>
      </Group>}
  </>
}

export default LuxPlusPreferredPaymentMethod
